import { Axis, Space, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

export class ObstacleRocketBehavior extends ObstacleBehavior {
  private rushing: boolean = false;
  private currentPathIndex: number;
  private lateralOffset: number;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig, lateralOffset: number = 0) {
    super('obstacleRush', startPosition, pathIndex, config);
    this.currentPathIndex = pathIndex;
    this.lateralOffset = lateralOffset;
  }

  update(deltaTime: number): void {
    if (!this.level || !this.level.player) return;

    if (!this.rushing) {
      const dist = Vector3.Distance(this.mesh.position, this.level.player.getPosition());
      if (dist < 40) {
        // Trigger range increased slightly
        this.rushing = true;
      }
    } else {
      // Rush towards player along path
      // Assuming player is at lower index or we just move backwards
      // Speed * 20 from previous implementation seems fast, lets keep it fast but controllable
      // The user said "speed" is speed multiplier.
      // We want to move along path indices.
      // Index Change = Speed * DeltaTime
      // But indices area not uniform distance.
      // Better to approximate or just use index speed.
      // If points are 1 unit apart, 1 index/sec = 1 unit/sec.
      // Let's assume constant speed for now.
      const moveSpeed = this.config.speed; // Adjust as needed

      // Rush BACKWARDS along path (towards start, assuming player is coming from start)
      // Actually, if obstacle is ahead, it should move towards player.
      // If player is at index 100, obstacle at 150. Obstacle moves 150 -> 100.
      this.currentPathIndex -= moveSpeed * deltaTime * 0.5; // 0.5 factor to adjust for index density

      if (this.currentPathIndex < 0) {
        this.currentPathIndex = 0;
        this.dispose(); // Reached start of path
        return;
      }

      // Calculate position at currentPathIndex
      const idx = Math.floor(this.currentPathIndex);
      const nextIdx = Math.min(idx + 1, this.level.pathPoints.length - 1);
      const t = this.currentPathIndex - idx;

      const p1 = this.level.pathPoints[idx];
      const p2 = this.level.pathPoints[nextIdx];

      // Interpolated position on path center
      const currentPos = Vector3.Lerp(p1, p2, t);

      // Interpolated tangent
      // For smoother rotation, look at a point slightly ahead (or behind since we move backward)
      // Tangent direction for the obstacle facing: it should face the player (tangent backwards)
      // Tangent at index: p2 - p1 (forward). We want backward: p1 - p2.
      const tangent = p1.subtract(p2).normalize();

      // Calculate lateral vector (same as generator)
      // Generator used: tangent = next - prev. Here we use segment tangent.
      // Forward tangent for lateral calc:
      const forwardTangent = p2.subtract(p1).normalize();
      const lateral = new Vector3(-forwardTangent.z, 0, forwardTangent.x);

      // Apply offsets
      const finalPos = currentPos.subtract(lateral.scale(this.lateralOffset));

      finalPos.y += this.config.yOffset;

      this.mesh.position = finalPos;

      // Align mesh to path (face movement direction)
      // We are moving backwards, so we face backwards (towards player)
      this.mesh.lookAt(this.mesh.position.add(tangent));
      if (this.config.rotation?.y !== 0) {
        this.mesh.rotate(Axis.Y, this.config.rotation.y, Space.LOCAL);
      }

      // if (this.rushing) {
      //   this.level.game.sound.play('obstacleengine', this.mesh);
      // }
    }
  }

  initLevel(level: Level): ObstacleRocketBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('obstacleengine', this.mesh).then((sound) => {
      if (sound) {
        sound.loop = true;
        sound.play();
      }
    });
    return this;
  }

  clone(): ObstacleRocketBehavior {
    return new ObstacleRocketBehavior(this.startPosition, this.pathIndex, this.config, this.lateralOffset);
  }

  dispose() {
    if (this.mesh) {
      this.mesh.dispose();
    }
  }
}
